import React, { useMemo, useState } from "react";
import { TableHead } from "@/components/ui/table";

export const fmtInt = (n) => (n || 0).toLocaleString("en-IN");
export const fmtCur = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const STRING_SORT_KEYS = new Set(["username", "city", "language", "notes"]);
const DATE_SORT_KEYS = new Set(["live_date"]);

export function useSortedList(list, initial = null) {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    if (!sort?.key) return list;
    const s = sort.dir === "desc" ? -1 : 1;
    const key = sort.key;
    return [...list].sort((a, b) => {
      let av = a?.[key];
      let bv = b?.[key];
      if (STRING_SORT_KEYS.has(key)) {
        av = (av || "").toString().toLowerCase();
        bv = (bv || "").toString().toLowerCase();
        return av < bv ? -1 * s : av > bv ? 1 * s : 0;
      }
      if (DATE_SORT_KEYS.has(key)) {
        av = av ? new Date(av).getTime() : -Infinity;
        bv = bv ? new Date(bv).getTime() : -Infinity;
      } else {
        av = Number(av || 0);
        bv = Number(bv || 0);
      }
      if (Number.isNaN(av)) av = -Infinity;
      if (Number.isNaN(bv)) bv = -Infinity;
      return av < bv ? -1 * s : av > bv ? 1 * s : 0;
    });
  }, [list, sort]);
  const toggle = (key) =>
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  return { sorted, sort, toggle };
}

export function SortHead({ label, k, sort, onToggle, className = "" }) {
  const active = sort?.key === k;
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "↕";
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onToggle(k)}
        className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${active ? "text-blue-600" : "text-slate-500"}`}
        data-testid={`sort-${k}`}
      >
        <span>{label}</span>
        <span className={`text-[9px] ${active ? "" : "opacity-40"}`}>{arrow}</span>
      </button>
    </TableHead>
  );
}

export function PlatformBadge({ p }) {
  if (!p) return null;
  const styles = {
    Instagram: "bg-pink-50 text-pink-700 border-pink-200",
    YouTube: "bg-rose-50 text-rose-700 border-rose-200",
    LinkedIn: "bg-blue-50 text-blue-700 border-blue-200",
    X: "bg-slate-100 text-slate-800 border-slate-300",
  };
  return (
    <span
      className={`ml-1.5 inline-block px-1.5 py-0.5 rounded-full border text-[9px] font-semibold uppercase tracking-wider align-middle ${styles[p] || "bg-slate-100 text-slate-700 border-slate-200"}`}
      data-testid="creator-platform-badge"
    >
      {p}
    </span>
  );
}

export function MarginPill({ internal, client }) {
  const margin = (Number(client) || 0) - (Number(internal) || 0);
  const positive = margin >= 0;
  const cls = positive
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-rose-50 text-rose-700 border-rose-200";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-mono font-semibold ${cls}`}>
      {fmtCur(margin)}
    </span>
  );
}
